// 表格分页（#133）。每页行数可选 20、50、100，默认 50，浏览器按表格分别记住（用户 2026-09-25 要求）。三种来源：
// 数据已经全量取回的列表在前端切页（usePage）；会无限增长的列表按页向后端取，多取一条判断有没有下一页（总数另取，见下）：
// id 单调的审计与流水用游标（useCursorPage），翻页期间写入的新记录不会让下一页重复，也没有深度上限；消息与用量汇总用 offset（useOffsetPage，#135）。
// 过滤或搜索条件变了、每页行数变了都回到第 1 页：调用方把条件作为 reset 传进来，条件的 JSON 变了就重置。
// 每个 hook 的第一个参数是表格名，用来记住每页行数；返回值可以直接展开给 Pager。前端切页的列表知道总数（total），分页栏显示“共 N 条”；
// 后端分页的列表另从各自的 count 接口取总数（决策 D182），用 totalOf 转成 Pager 的 total 与 capped。
import { useState } from "react";
import { readPref, writePref } from "@/app/storage";

/** 列表总数接口的响应（决策 D182）：count 为空表示没统计出来（消息计数超时），capped 为真表示数到上限就停了、实际更多。 */
export interface ListCount { count: number | null; capped: boolean }

/** 把总数接口的结果转成 Pager 的 total 与 capped；还没取到或没统计出来就不显示总数。 */
export function totalOf(c: ListCount | undefined): { total?: number; capped?: boolean } {
  return c?.count == null ? {} : { total: c.count, capped: c.capped };
}

/** 每页行数的选项。 */
export const PAGE_SIZES = [20, 50, 100] as const;
/** 没有记住过时的每页行数。 */
export const DEFAULT_PAGE_SIZE = 50;

const storageKey = (table: string) => `axon.pageSize.${table}`;

/** 读记住的每页行数，值不在选项里时用默认值。 */
function storedSize(table: string): number {
  const n = Number(readPref(storageKey(table)));
  return (PAGE_SIZES as readonly number[]).includes(n) ? n : DEFAULT_PAGE_SIZE;
}

/** 每页行数，改了写进浏览器存储。 */
function usePageSize(table: string): [number, (size: number) => void] {
  const [size, setSize] = useState(() => storedSize(table));
  return [size, (n: number) => {
    setSize(n);
    writePref(storageKey(table), String(n));
  }];
}

/** 当前页，条件变了自动回到第 1 页。 */
function usePageState(reset: unknown): [number, (page: number) => void] {
  const key = JSON.stringify(reset ?? null);
  const [state, setState] = useState({ key, page: 1 });
  return [state.key === key ? state.page : 1, (page: number) => setState({ key, page })];
}

/** 全量列表在前端分页：返回这一页的行、页数、每页行数与总数。 */
export function usePage<T>(table: string, rows: T[], reset?: unknown) {
  const [pageSize, onPageSize] = usePageSize(table);
  const [page, onChange] = usePageState([reset ?? null, pageSize]);
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const current = Math.min(page, pageCount); // 删掉最后几行时不停在空页
  return { rows: rows.slice((current - 1) * pageSize, current * pageSize), page: current, pageCount, onChange, pageSize, onPageSize, count: rows.length, total: rows.length };
}

/** 后端返回的一页：多取的那一条不显示，只用来判断有没有下一页。 */
function trim<T>(rows: T[], page: number, pageSize: number) {
  const more = rows.length > pageSize;
  return { rows: rows.slice(0, pageSize), page, pageCount: more ? page + 1 : page, count: Math.min(rows.length, pageSize) };
}

/** 按 offset 向后端取：query 是接口的 limit、offset，拿到结果后用 view 取这一页。 */
export function useOffsetPage(table: string, reset?: unknown) {
  const [pageSize, onPageSize] = usePageSize(table);
  const [page, onChange] = usePageState([reset ?? null, pageSize]);
  return {
    page,
    query: { limit: pageSize + 1, offset: (page - 1) * pageSize },
    view: <T,>(rows: T[]) => ({ ...trim(rows, page, pageSize), onChange, pageSize, onPageSize }),
  };
}

/** 游标翻页的状态：看过的每一页的起点，条件或每页行数变了回到第 1 页。go 翻到第 p 页，往后翻时 next 是新一页的起点。 */
function useCursors<C>(table: string, reset: unknown) {
  const [pageSize, onPageSize] = usePageSize(table);
  const key = JSON.stringify([reset ?? null, pageSize]);
  const [state, setState] = useState<{ key: string; cursors: (C | undefined)[] }>({ key, cursors: [undefined] });
  const cursors = state.key === key ? state.cursors : [undefined];
  const page = cursors.length;
  const go = (p: number, next: C | undefined) => setState({ key, cursors: p <= page ? cursors.slice(0, p) : [...cursors, next] });
  return { pageSize, onPageSize, page, cursor: cursors[page - 1], go };
}

/** 按游标向后端取：limit 是要向接口要的条数，cursor 是这一页的起点（第 1 页为空），view 需要从一行取出游标。
 * 只能翻到看过的页或下一页，Pagination 也只显示这些页码。 */
export function useCursorPage<C>(table: string, reset?: unknown) {
  const { pageSize, onPageSize, page, cursor, go } = useCursors<C>(table, reset);
  return {
    page,
    limit: pageSize + 1,
    cursor,
    view<T>(rows: T[], cursorOf: (row: T) => C) {
      const shown = trim(rows, page, pageSize);
      const last = shown.rows[shown.rows.length - 1];
      const onChange = (p: number) => go(p, last === undefined ? undefined : cursorOf(last));
      return { ...shown, onChange, pageSize, onPageSize };
    },
  };
}

/** 按后端给的下一页起点取（消息列表，决策 D82）：后端凑满一页或用完扫描预算时返回 next，为空表示查完。
 * 预算用完时一页可能不满甚至为空，只要有 next 就能翻到下一页接着查。 */
export function useNextPage<C>(table: string, reset?: unknown) {
  const { pageSize, onPageSize, page, cursor, go } = useCursors<C>(table, reset);
  return {
    page,
    limit: pageSize,
    cursor,
    view<T>(rows: T[], next: C | null | undefined) {
      return { rows, page, pageCount: next ? page + 1 : page, count: rows.length, onChange: (p: number) => go(p, next ?? undefined), pageSize, onPageSize };
    },
  };
}
